import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  useContactProfile, useRegenerateProfile, useToggleContactProfile,
} from '../../hooks/useContacts.js';
import { ContactInteractionsModal } from './ContactInteractionsModal.js';
import { AddNoteModal } from './AddNoteModal.js';
import { LoadingState } from '../ui/LoadingState.js';
import { EmptyState } from '../ui/EmptyState.js';
import { Button } from '../ui/Button.js';

interface Props {
  contactId: number;
  contactName: string;
}

export function ContactProfileView({ contactId, contactName }: Props) {
  const { t } = useTranslation();

  const relativeTime = (dateStr: string | null): string => {
    if (!dateStr) return '—';
    const diff = Date.now() - new Date(dateStr).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 2) return t('common.timeAgo.justNow');
    if (minutes < 60) return t('common.timeAgo.minutes', { count: minutes });
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return t('common.timeAgo.hours', { count: hours });
    const days = Math.floor(hours / 24);
    return t('common.timeAgo.days', { count: days });
  };

  const { data: profile, isLoading } = useContactProfile(contactId);
  const regenerate = useRegenerateProfile();
  const toggleProfile = useToggleContactProfile();
  const [showInteractions, setShowInteractions] = useState(false);
  const [showAddNote, setShowAddNote] = useState(false);
  const [regenError, setRegenError] = useState('');

  if (isLoading) {
    return <LoadingState label={t('common.loading')} />;
  }

  const handleRegen = async () => {
    setRegenError('');
    try {
      await regenerate.mutateAsync(contactId);
    } catch (err) {
      setRegenError(err instanceof Error ? err.message : String(err));
    }
  };

  if (!profile || !profile.isActive) {
    return (
      <div
        className="rounded-xl p-6 text-center flex flex-col items-center gap-3"
        style={{ background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)' }}
      >
        <p className="text-sm" style={{ color: 'var(--evo-fg-3)' }}>
          {t('contacts.profile.not_active')}
        </p>
        <Button
          variant="solid"
          tone="cyan"
          size="sm"
          disabled={toggleProfile.isPending}
          onClick={() => toggleProfile.mutate({ contactId, isActive: true })}
        >
          {t('contacts.profile.activate')}
        </Button>
      </div>
    );
  }

  if (!profile.markdownContent) {
    return (
      <div
        className="rounded-xl p-6 text-center flex flex-col items-center gap-3"
        style={{ background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)' }}
      >
        <EmptyState size="sm" reserveSpace title={t('contacts.profile.empty')} />
        {regenError && (
          <p className="text-xs" style={{ color: 'var(--evo-danger)' }}>{regenError}</p>
        )}
        <Button
          variant="solid"
          tone="cyan"
          size="sm"
          loading={regenerate.isPending}
          onClick={() => void handleRegen()}
        >
          {regenerate.isPending ? t('contacts.profile.regenerating') : t('contacts.profile.generate_now')}
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <span className="text-xs" style={{ color: 'var(--evo-fg-3)' }}>
            {t('contacts.profile.last_updated')}: {relativeTime(profile.lastUpdatedAt)}
          </span>
          <div className="flex items-center gap-1.5 flex-wrap">
            <Button variant="outline" tone="muted" size="sm" onClick={() => setShowAddNote(true)}>
              {t('contacts.profile.add_note')}
            </Button>
            <Button variant="outline" tone="muted" size="sm" onClick={() => setShowInteractions(true)}>
              {t('contacts.profile.view_log')}
            </Button>
            <Button
              variant="solid"
              tone="cyan"
              size="sm"
              loading={regenerate.isPending}
              onClick={() => void handleRegen()}
            >
              {regenerate.isPending ? t('contacts.profile.regenerating') : t('contacts.profile.regenerate')}
            </Button>
          </div>
        </div>

        {regenError && (
          <p className="text-xs" style={{ color: 'var(--evo-danger)' }}>{regenError}</p>
        )}

        <div
          className="rounded-lg p-4 text-xs leading-relaxed"
          style={{ background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)', color: 'var(--evo-fg-2)' }}
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h1: ({ children }) => (
                <h1 className="text-sm font-bold mb-2 mt-3 first:mt-0" style={{ color: 'var(--evo-fg-2)' }}>{children}</h1>
              ),
              h2: ({ children }) => (
                <h2 className="text-xs font-semibold mb-1.5 mt-4 first:mt-0 uppercase tracking-wide" style={{ color: 'var(--evo-cyan)' }}>{children}</h2>
              ),
              h3: ({ children }) => (
                <h3 className="text-xs font-semibold mb-1 mt-2" style={{ color: 'var(--evo-fg-2)' }}>{children}</h3>
              ),
              h4: ({ children }) => (
                <h4 className="text-xs font-medium mb-1 mt-2" style={{ color: 'var(--evo-fg-2)' }}>{children}</h4>
              ),
              p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
              ul: ({ children }) => <ul className="list-disc ml-4 mb-2">{children}</ul>,
              ol: ({ children }) => <ol className="list-decimal ml-4 mb-2">{children}</ol>,
              li: ({ children }) => <li className="mb-0.5">{children}</li>,
              code: ({ children }) => (
                <code className="px-1 rounded" style={{ background: 'rgba(0,0,0,0.3)' }}>{children}</code>
              ),
              pre: ({ children }) => (
                <pre className="p-2 rounded overflow-x-auto my-2 text-xs" style={{ background: 'rgba(0,0,0,0.3)' }}>{children}</pre>
              ),
              strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
              blockquote: ({ children }) => (
                <blockquote
                  className="border-l-2 pl-3 my-2 italic"
                  style={{ borderColor: 'var(--evo-stroke)', color: 'var(--evo-fg-3)' }}
                >
                  {children}
                </blockquote>
              ),
            }}
          >
            {profile.markdownContent}
          </ReactMarkdown>
        </div>
      </div>

      <ContactInteractionsModal
        contactId={contactId}
        contactName={contactName}
        isOpen={showInteractions}
        onClose={() => setShowInteractions(false)}
      />
      <AddNoteModal
        contactId={contactId}
        isOpen={showAddNote}
        onClose={() => setShowAddNote(false)}
      />
    </>
  );
}
